/*
 * <nl-w2-hotspots data-sheet="SheetHostId"> (sections/nl-w2-hotspots.liquid)
 *   [data-hot-point="n"] > button[data-hot-dot][aria-controls][aria-expanded] + [data-hot-pop] (hidden)
 *   [data-hot-item="n"]  numbered list rows (highlight sync with the dots)
 *   <nl-dialog id=SheetHostId> with [data-hot-sheet-body] (phones)
 *
 * Disclosure pattern, one popover open at a time. >= 768 px: the popover opens next to the dot (side chosen in Liquid),
 * Escape or an outside click closes it and focus returns to the dot. < 768 px: the dot opens the bottom sheet
 * (foundation <nl-dialog>: modal, focus trap, scroll lock) with a clone of the popover content; the cloned
 * <product-form> upgrades itself, quick add keeps working and the cart drawer replaces the sheet.
 * Theme editor: selecting a hotspot block opens it, deselecting closes it.
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-hotspots')) return;

  const mqPhone = window.matchMedia('(max-width: 767px)');

  class W2Hotspots extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.points = Array.from(this.querySelectorAll('[data-hot-point]'));
      if (!this.points.length) return;
      this._bound = true;
      this.sheetHost = this.dataset.sheet ? document.getElementById(this.dataset.sheet) : null;
      this.sheetBody = this.sheetHost ? this.sheetHost.querySelector('[data-hot-sheet-body]') : null;
      this.open = null;

      this.points.forEach((point) => {
        const dot = point.querySelector('[data-hot-dot]');
        if (!dot) return;
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(point);
        });
        point.addEventListener('pointerenter', () => this.highlight(point.dataset.hotPoint, true));
        point.addEventListener('pointerleave', () => this.highlight(point.dataset.hotPoint, false));
        // Keyboard users tabbing past the popover close it (focus never gets trapped in a non modal popover)
        point.addEventListener('focusout', (event) => {
          if (this.open === point && event.relatedTarget && !point.contains(event.relatedTarget)) this.close();
        });
      });

      this.querySelectorAll('[data-hot-item]').forEach((item) => {
        const id = item.dataset.hotItem;
        item.addEventListener('pointerenter', () => this.highlight(id, true));
        item.addEventListener('pointerleave', () => this.highlight(id, false));
        item.addEventListener('focusin', () => this.highlight(id, true));
        item.addEventListener('focusout', () => this.highlight(id, false));
      });

      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.open) return;
        const dot = this.open.querySelector('[data-hot-dot]');
        this.close();
        if (dot) dot.focus();
      };
      this.onDocClick = (event) => {
        if (!this.open || this.open.contains(event.target)) return;
        this.close();
      };
      this.onSheetClose = () => {
        if (this.sheetPoint) {
          const dot = this.sheetPoint.querySelector('[data-hot-dot]');
          if (dot) dot.setAttribute('aria-expanded', 'false');
          this.sheetPoint = null;
        }
      };
      this.onBreakpoint = () => this.close();
      this.onBlockSelect = (event) => {
        const point = event.target && event.target.closest ? event.target.closest('[data-hot-point]') : null;
        if (point && point !== this.open && !mqPhone.matches) this.show(point);
      };
      this.onBlockDeselect = () => this.close();

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
      if (this.sheetHost) this.sheetHost.addEventListener('dialog:close', this.onSheetClose);
      if (mqPhone.addEventListener) mqPhone.addEventListener('change', this.onBreakpoint);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      if (!this._bound) return;
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocClick);
      if (this.sheetHost) this.sheetHost.removeEventListener('dialog:close', this.onSheetClose);
      if (mqPhone.removeEventListener) mqPhone.removeEventListener('change', this.onBreakpoint);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this._bound = false;
    }

    highlight(id, state) {
      if (!id) return;
      const point = this.querySelector(`[data-hot-point="${id}"]`);
      const item = this.querySelector(`[data-hot-item="${id}"]`);
      if (point) point.toggleAttribute('data-highlight', state);
      if (item) item.toggleAttribute('data-highlight', state);
    }

    toggle(point) {
      if (mqPhone.matches && this.sheetHost && typeof this.sheetHost.open === 'function') {
        this.openSheet(point);
        return;
      }
      if (this.open === point) this.close();
      else this.show(point);
    }

    show(point) {
      this.close();
      const dot = point.querySelector('[data-hot-dot]');
      const pop = point.querySelector('[data-hot-pop]');
      if (!dot || !pop) return;
      pop.hidden = false;
      point.setAttribute('data-open', '');
      dot.setAttribute('aria-expanded', 'true');
      this.open = point;
    }

    close() {
      const point = this.open;
      if (!point) return;
      const dot = point.querySelector('[data-hot-dot]');
      const pop = point.querySelector('[data-hot-pop]');
      if (pop) pop.hidden = true;
      if (dot) dot.setAttribute('aria-expanded', 'false');
      point.removeAttribute('data-open');
      this.open = null;
    }

    openSheet(point) {
      const pop = point.querySelector('[data-hot-pop]');
      const dot = point.querySelector('[data-hot-dot]');
      if (!pop || !this.sheetBody) return;
      this.close();
      const fragment = document.createDocumentFragment();
      Array.from(pop.childNodes).forEach((node) => fragment.appendChild(node.cloneNode(true)));
      this.sheetBody.replaceChildren(fragment);
      this.sheetPoint = point;
      if (dot) dot.setAttribute('aria-expanded', 'true');
      this.sheetHost.open(dot);
    }
  }

  customElements.define('nl-w2-hotspots', W2Hotspots);
})();
